/**
 * ⚠️：三个步骤的公共部分：
 * 1，table数据的部分加载逻辑
 * 2，toast提示逻辑
 * 3，next逻辑
 */
import { statistics } from '@/api/dot/flow';
import { sentErrCheck, getUniformTaskStatus } from '@/api/qc/qcflow';
import { mapState } from 'vuex';
import { rebuildEditorErrors, filterLowErrorType } from '@/util/dot';
import { SUBMIT_STATUS, STEP } from './const';
import { ERROE_EMBED } from '@/components/rich-text/const';
import { fieldsCheck } from '@/components/rich-text/utils/check';
import { jsonTextToText } from '@/components/rich-text';
import { hasValidChar } from '@/util/string';

const STATISTICS_TYPE = {
  0: 'low_err_submit',
  1: 'term_abs_submit',
  2: 'high_frequency_submit',
};

const LIMIT = 3;
const PARTIAL_LOAD_COUNT = 10;

export default {
  props: ['queryParams', 'flowCount'],
  data() {
    return {
      LIMIT,
      STEP,
      records: [],
      // 用来做分步加载的临时列表
      tempList: [],
      page: {
        current: 1,
        size: 50,
        total: 0,
      },
      errorCount: 0,
      loading: false,
      submitStatusEnum: SUBMIT_STATUS,
      qcName: this.queryParams.taskId,
      staParams: {
        time: new Date().getTime(),
        browser: navigator.userAgent.toLowerCase(),
        url: document.URL,
        scene_name: '智能检查',
      },
      canSubmit: true,
      multiSelection: [],
      fullLoading: false,
    };
  },
  computed: {
    ...mapState({
      userInfo: (state) => state.user.userInfo,
    }),
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.curPath = to.path;
      vm.oldPath = from.path;
    });
  },
  methods: {
    handleSelectionChange(val) {
      this.multiSelection = val;
    },
    clearCustomError(json) {
      const copyJson = JSON.parse(JSON.stringify(json));
      return copyJson.map((item) => {
        if (
          item.attributes &&
          item.attributes[ERROE_EMBED.custom_error] !== undefined
        ) {
          delete item.attributes[ERROE_EMBED.custom_error];
        }
        return item;
      });
    },
    handleChange(text, json, row) {
      row.editedText = text;
      row.editedJson = JSON.stringify(this.clearCustomError(json));
    },
    handleBlur(text, json, row) {
      row.editedText = text;
      row.editedJson = JSON.stringify(this.clearCustomError(json));
      sentErrCheck({
        texts: [
          {
            sentId: row.sentId ?? row.id,
            src: row.srcText ?? row.srcTxt,
            tgt: row.editedJson,
          },
        ],
        srcLan: this.queryParams.slan,
        tgtLan: this.queryParams.tlan,
        orderId: this.queryParams.id,
        saveError: false,
        spell: false,
        checkMiss: true,
        showMiss: true,
      }).then((res) => {
        const usableData = res.data?.data ?? {};
        if (usableData && usableData.length) {
          const lowErrList = filterLowErrorType(usableData[0]?.low_error_list);
          row.errors = rebuildEditorErrors(lowErrList);
        } else {
          row.errors = [];
        }
      });
    },
    // 域匹配检查
    sentDigitCheck(row) {
      const peJsonText = JSON.parse(row.editedJson ?? row.qcText);
      const isMatched = fieldsCheck(peJsonText);
      if (!isMatched) {
        this.$message.error(
          '提交失败，译文框的内容中检测存在域没有成对出现、域开闭顺序不对、域交叉等问题',
        );
      }
      return isMatched;
    },
    batchSubmitCheck() {
      return new Promise((resolve, reject) => {
        // 1. 域匹配校验
        const isAllMatched = this.multiSelection?.every((item) => {
          return this.sentDigitCheck(item);
        });
        if (!isAllMatched) {
          reject({ isAllMatched: false });
        }

        // 2. 译文为空校验
        const isAllFilled = this.multiSelection?.every((item) => {
          return hasValidChar(item.editedText);
        });
        if (!isAllFilled) {
          this.$confirm(
            '<h4>存在译文为空的句子</h4><div>请谨慎操作！译文为空的场景一般应用于原文断句异常时，已将此句的译文合并入其他句子中。</div>',
            '译文为空预警',
            {
              dangerouslyUseHTMLString: true,
              confirmButtonText: '确定',
              cancelButtonText: '取消',
              type: 'warning',
            },
          )
            .then(() => {
              resolve();
            })
            .catch(() => {
              this.multiSelection.forEach((item) => {
                if (item.editedText?.trim() === '') {
                  item.editedText = item.qcText && jsonTextToText(item.qcText);
                  item.editedJson = item.qcText;
                  item.errors = item.errDtoList
                    ? rebuildEditorErrors(item.errDtoList)
                    : [];
                  item.updateSign++;
                }
                reject({ isAllFilled: false });
              });
            });
        }

        if (isAllFilled && isAllMatched) resolve();
      });
    },
    next(curStep) {
      // 上报
      statistics(
        Object.assign(
          {
            event_type: 'click',
            event_name: STATISTICS_TYPE[curStep],
            ref_url: this.oldPath,
            event_params: {
              order_id: this.queryParams.id,
              qcTaskId: this.queryParams.taskId,
            },
            u_id: this.userInfo?.staffId,
          },
          this.staParams,
        ),
      );
      this.$emit('next');
    },
    handleCurrentChange() {
      this.fetchListData();
    },
    // 展开收起
    changeShowAll(row) {
      row.isRendering = true;
      // 渲染有耗时，先给个loading，再在下一个渲染中展开
      setTimeout(() => {
        row.showAll = !row.showAll;
        if (row.showAll) {
          row.tarLimit = 'infinite';
        } else {
          row.tarLimit = LIMIT;
        }
        row.isRendering = false;
      }, 0);
    },
    // 分步加载
    partialLoad(list) {
      if (list.length <= 0) return;

      this.backTop();
      const curPart = list.splice(0, PARTIAL_LOAD_COUNT);
      this.records = this.records.concat(curPart);
      // 需要用宏任务来确保在loading的DOM消失后加载剩余内容
      setTimeout(() => {
        this.partialLoad(list);
      }, 0);
    },
    backTop() {
      document.querySelector('.backTop').scrollTop = 0;
    },
    // 批量更新后不刷新列表，前端更新数据
    updateSelectionStatus() {
      this.multiSelection.forEach(
        (item) => (item.qcStatus = this.submitStatusEnum.done),
      );
      this.$refs.table?.clearSelection();
    },
    updateTaskStatus(step) {
      getUniformTaskStatus({
        orderId: this.queryParams.id,
        qcTaskId: this.queryParams.taskId,
        // 前端从0开始，这里传给后台是从1开始
        currentFlow: step + 1,
      }).then((res) => {
        const { hitCount, maxLimit } = res.data?.data ?? {};
        this.canSubmit = hitCount;
        this.errorCount = maxLimit;
      });
    },
  },
};
